import {
  AcceptOrderPayload,
  OrderResponse,
  OrdersQuery,
  OrderStateResponse,
  PreCreateOrder,
  UpdateOrderPayload,
  OrderReviewPayload,
  ReviewsResponse,
  MerchantStats,
  OrderUpdate,
  CreateOrderResponse,
  CreateQuickTradePayload,
  acceptQuickTradeByMerchantPayload,
  QuickTrade,
  QuickTradeStatus,
  GetQuickRatePayload,
  QuickRateResult,
  RejectQuickTradePayload,
  OrderPayload,
} from '@sdk/shared/types/order.types';
import { handleServiceError } from '@sdk/utils/error-handler';
import {
  processMerchantStats,
  processReviews,
  processPreCountedReviews,
} from '@sdk/shared/lib/merchant-stats';
import { Auth } from '@sdk/auth';
import { AxiosInstance } from 'axios';
import { QuickTradeNotificationData } from '@sdk/notification/types';

export class orderService {
  private auth: Auth;

  constructor(auth: Auth) {
    this.auth = auth;
  }

  createOrder = async (payload: OrderPayload) => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.post('/orders/order', payload);
      if (!data) {
        throw new Error('Failed to create order');
      }
      return data as CreateOrderResponse;
    });
  };
  getOrder = async (orderHash: string) => {
    return handleServiceError(async () => {
      const { data }: { data: OrderResponse } = await this.auth.protectedAxios.get(
        '/orders/order/' + orderHash,
      );
      if (!data) {
        throw new Error('Failed to fetch order');
      }
      // Process merchant stats if available
      const stats = data.merchantStats ? processMerchantStats(data.merchantStats) : null;
      const reviewStats = data.merchantReviews
        ? processPreCountedReviews(data.merchantReviews)
        : undefined;
      return {
        ...data,
        orderStats: {
          totalOrders: stats?.totalOrders || 0,
          completionRate: stats?.completionRate || '0',
          avgReleaseTime: stats?.avgReleaseTime || 'N/A',
          avgPayTime: stats?.avgPayTime || 'N/A',
          positiveRates: reviewStats?.positiveRate || 0,
          timeLimit: data.ad.timeLimit,
        },
      } satisfies OrderResponse;
    });
  };
  getOrderState = async (orderHash: string) => {
    return handleServiceError(async () => {
      const { data }: { data: OrderStateResponse } = await this.auth.protectedAxios.get(
        '/orders/order/' + orderHash + '/state',
      );
      if (!data) {
        throw new Error('Failed to fetch order');
      }
      return data;
    });
  };
  getOrders = async (query: OrdersQuery) => {
    if (!query.orderBy) {
      query.orderBy = 'createdAt';
    }
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.get<OrderResponse[]>('/orders', {
        params: query,
      });
      if (!data) {
        throw new Error('Failed to fetch orders');
      }
      return data.map(order => {
        const stats = order.merchantStats ? processMerchantStats(order.merchantStats) : null;
        const reviewStats = order.merchantReviews
          ? processPreCountedReviews(order.merchantReviews)
          : undefined;
        return {
          ...order,
          orderStats: {
            totalOrders: stats?.totalOrders || 0,
            completionRate: stats?.completionRate || '0',
            avgReleaseTime: stats?.avgReleaseTime || 'N/A',
            avgPayTime: stats?.avgPayTime || 'N/A',
            positiveRates: reviewStats?.positiveRate || 0,
            positiveRate: reviewStats?.positiveRate || 0,
            timeLimit: order.ad.timeLimit,
          },
        };
      });
    });
  };
  updateOrder = async (orderHash: string, payload: UpdateOrderPayload) => {
    return handleServiceError<OrderUpdate>(async () => {
      const { data } = await this.auth.protectedAxios.post('orders/order/' + orderHash, payload);
      if (!data) {
        throw new Error('Failed to update order');
      }
      return data;
    });
  };
  acceptOrder = async (orderHash: string, payload: AcceptOrderPayload) => {
    return handleServiceError<OrderUpdate>(async () => {
      const { data } = await this.auth.protectedAxios.post('orders/order/' + orderHash, payload);
      if (!data) {
        throw new Error('Failed to accept order');
      }
      return data;
    });
  };
  reviewOrder = async (orderHash: string, review: OrderReviewPayload) => {
    return handleServiceError<OrderUpdate>(async () => {
      const { data } = await this.auth.protectedAxios.post(
        `/orders/order/${orderHash}/review`,
        review,
      );
      if (!data) {
        throw new Error('Failed to submit review');
      }
      return data;
    });
  };
  getReviews = async (address: string) => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.get<ReviewsResponse>(
        `/orders/reviews/${address}`,
      );
      if (!data) {
        throw new Error('Failed to fetch reviews');
      }
      return data;
    });
  };
  getMerchantStats = async (address: string) => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.get<MerchantStats>(
        `/orders/stats/${address}`,
      );
      if (!data) {
        throw new Error('Failed to fetch merchant stats');
      }
      // Extract review stats from numbered keys
      const reviewStats = Object.keys(data)
        .filter(key => !isNaN(Number(key)))
        .map(key => ({
          _count: data[key]._count,
          revieweeId: data[key].revieweeId,
          score: data[key].score,
        }));
      // Process reviews using the existing function
      const processedReviews = processReviews(reviewStats);
      // Extract merchant stats
      const { totalOrders, paid, released, accepted, address: merchantAddress } = data;
      return {
        data: {
          merchantStats: {
            totalOrders,
            paid,
            released,
            accepted,
            address: merchantAddress,
            reviews: {
              positive: processedReviews.positive,
              negative: processedReviews.negative,
              total: processedReviews.total,
            },
          },
          reviewStats,
        },
      };
    });
  };
  createQuickTrade = async (payload: CreateQuickTradePayload) => {
    return handleServiceError<QuickTrade>(async () => {
      const { data } = await this.auth.protectedAxios.post('/quick-trade', payload);
      if (!data) {
        throw new Error('Failed to create quick trade');
      }
      return data as QuickTrade;
    });
  };
  acceptQuickTradeByMerchant = async (payload: acceptQuickTradeByMerchantPayload) => {
    return handleServiceError<CreateOrderResponse>(async () => {
      const { data } = await this.auth.protectedAxios.post('/quick-trade/accept', payload);
      if (!data) {
        throw new Error('Failed to accept quick order');
      }
      return data;
    });
  };
  getQuickTrade = async (quickTradeId: string) => {
    return handleServiceError<QuickTrade>(async () => {
      const { data } = await this.auth.protectedAxios.get<QuickTrade>(
        `/quick-trade/${quickTradeId}`,
      );
      if (!data) {
        throw new Error('Failed to fetch quick trade');
      }
      return data;
    });
  };
  getUserQuickTrade = async () => {
    return handleServiceError<QuickTradeNotificationData>(async () => {
      const { data } = await this.auth.protectedAxios.get<QuickTradeNotificationData>(
        `/quick-trade`,
      );
      if (!data) {
        throw new Error('Failed to fetch quick trade');
      }
      return data;
    });
  };
  getQuickTradeStatus = async (quickTradeId: string) => {
    return handleServiceError<QuickTradeStatus>(async () => {
      const { data } = await this.auth.protectedAxios.get<QuickTradeStatus>(
        `/quick-trade/${quickTradeId}/status`,
      );
      if (!data) {
        throw new Error('Failed to fetch quick order status');
      }
      return data;
    });
  };
  getQuickRate = async (params: GetQuickRatePayload) => {
    return handleServiceError<QuickRateResult>(async () => {
      const { data } = await this.auth.publicAxios.get<QuickRateResult>('/quick-trade/rate', {
        params,
      });
      if (!data) {
        throw new Error('Failed to fetch quick rate');
      }
      return data;
    });
  };
  cancelQuickTrade = async (quickTradeId: string) => {
    return handleServiceError<{ success: boolean; message: string }>(async () => {
      const { data } = await this.auth.protectedAxios.post(`/quick-trade/${quickTradeId}/cancel`);
      if (!data) {
        throw new Error('Failed to cancel quick trade');
      }
      return data;
    });
  };
  rejectQuickTrade = async (quickTradeId: string, payload: RejectQuickTradePayload) => {
    return handleServiceError<{ success: boolean; message: string }>(async () => {
      const { data } = await this.auth.protectedAxios.post(
        `/quick-trade/${quickTradeId}/reject`,
        payload,
      );
      if (!data) {
        throw new Error('Failed to reject quick trade');
      }
      return data;
    });
  };
  createSingleQuickTrade = async (payload: { adId: number }) => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.post('/quick-trade/single', payload);
      if (!data) {
        throw new Error('Failed to create single quick trade');
      }
      return data;
    });
  };
}
